const LAYOUT_CLASSES = [
  'blog-article-grid__item--featured',
  'blog-article-grid__item--side-a',
  'blog-article-grid__item--side-b',
  'blog-article-grid__item--bottom',
];

class BlogArticleGrid extends HTMLElement {
  connectedCallback() {
    this.itemsContainer = this.querySelector('[data-blog-article-grid-items]');
    this.showMoreButton = this.querySelector('[data-blog-article-grid-show-more]');
    this.sortLabel = this.querySelector('[data-sort-label]');
    this.listbox = this.querySelector('[data-blog-sort-listbox]');
    this.emptyState = this.querySelector('[data-blog-article-grid-empty]');
    this.tabs = [...this.querySelectorAll('[data-filter-tag]')];

    this.activeSort = 'newest';
    this.activeFilter = '';
    this.isLoading = false;

    this.listbox?.addEventListener('listbox:change', (event) => this.onSortChange(event));
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-blog-article-grid-show-more]')) {
        this.onShowMore();
      }
    });
    this.tabs.forEach((tab) => tab.addEventListener('click', () => this.onTabClick(tab)));

    const activeTab = this.tabs.find((tab) => tab.getAttribute('aria-current') === 'true');
    this.activeFilter = activeTab?.dataset.filterTag || '';

    this.applySort();
    this.applyFilter();
    this.updatePlaceholders();
    this.toggleEmptyState();
    this.updateLayoutClasses();
  }

  get items() {
    return [...this.itemsContainer.querySelectorAll('.blog-article-grid__item')];
  }

  onSortChange(event) {
    this.activeSort = event.detail.value;
    const selectedOption = this.listbox.querySelector('[aria-selected="true"]');

    if (selectedOption && this.sortLabel) {
      this.sortLabel.textContent = selectedOption.textContent.trim();
    }

    this.applySort();
    this.applyFilter();
    this.updatePlaceholders();
    this.toggleEmptyState();
    this.updateLayoutClasses();
  }

  onTabClick(tab) {
    this.activeFilter = tab.dataset.filterTag || '';
    this.tabs.forEach((item) => item.setAttribute('aria-current', item === tab ? 'true' : 'false'));
    this.updateCategoryParam(this.activeFilter);

    this.applySort();
    this.applyFilter();
    this.updatePlaceholders();
    this.toggleEmptyState();
    this.updateLayoutClasses();
  }

  updateCategoryParam(tag) {
    const url = new URL(window.location.href);

    if (tag) {
      url.searchParams.set('category', tag);
    } else {
      url.searchParams.delete('category');
    }

    history.replaceState({}, '', url.toString());
  }

  updatePlaceholders() {
    const showPlaceholders = this.activeSort === 'newest' && !this.activeFilter;

    this.items.forEach((item) => {
      if (item.dataset.placeholder !== 'true') {
        return;
      }

      item.toggleAttribute('hidden', !showPlaceholders);
    });
  }

  applyFilter() {
    let visibleCount = 0;

    this.items.forEach((item) => {
      if (item.dataset.placeholder === 'true') {
        return;
      }

      if (!this.activeFilter) {
        item.removeAttribute('hidden');
        visibleCount += 1;
        return;
      }

      const tags = (item.dataset.tags || '').split(',').filter(Boolean);
      const matches = tags.includes(this.activeFilter);

      item.toggleAttribute('hidden', !matches);

      if (matches) {
        visibleCount += 1;
      }
    });

    this.visibleItemsCount = visibleCount;
  }

  applySort() {
    const placeholders = this.items.filter((item) => item.dataset.placeholder === 'true');
    const realItems = this.items.filter((item) => item.dataset.placeholder !== 'true');
    const sorted = [...realItems].sort((a, b) => this.compareItems(a, b));

    sorted.forEach((item) => this.itemsContainer.appendChild(item));
    placeholders.forEach((item) => this.itemsContainer.appendChild(item));
  }

  compareItems(a, b) {
    const indexA = parseInt(a.dataset.originalIndex, 10) || 0;
    const indexB = parseInt(b.dataset.originalIndex, 10) || 0;
    const titleA = (a.dataset.title || '').toLowerCase();
    const titleB = (b.dataset.title || '').toLowerCase();
    const dateA = parseInt(a.dataset.publishedAt, 10) || 0;
    const dateB = parseInt(b.dataset.publishedAt, 10) || 0;

    switch (this.activeSort) {
      case 'newest':
        return dateB - dateA;
      case 'oldest':
        return dateA - dateB;
      case 'title-ascending':
        return titleA.localeCompare(titleB);
      case 'title-descending':
        return titleB.localeCompare(titleA);
      default:
        return indexA - indexB;
    }
  }

  updateLayoutClasses() {
    const useOriginalLayout = this.activeSort === 'newest';
    const shouldUseFilteredLayout = useOriginalLayout && Boolean(this.activeFilter);
    let visibleIndex = 0;

    this.items.forEach((item) => {
      LAYOUT_CLASSES.forEach((cls) => item.classList.remove(cls));

      if (item.hasAttribute('hidden')) {
        return;
      }

      if (shouldUseFilteredLayout && item.dataset.placeholder !== 'true') {
        if (visibleIndex === 0) {
          item.classList.add('blog-article-grid__item--featured');
          this.updateCardVariant(item, 'featured');
        } else if (visibleIndex === 1) {
          item.classList.add('blog-article-grid__item--side-a');
          this.updateCardVariant(item, 'standard');
        } else if (visibleIndex === 2) {
          item.classList.add('blog-article-grid__item--side-b');
          this.updateCardVariant(item, 'standard');
        } else {
          item.classList.add('blog-article-grid__item--bottom');
          this.updateCardVariant(item, 'standard');
        }

        visibleIndex += 1;
      } else if (useOriginalLayout && item.dataset.layoutClass) {
        item.classList.add(item.dataset.layoutClass);
        this.updateCardVariant(item, item.dataset.layoutClass === 'blog-article-grid__item--featured' ? 'featured' : 'standard');
      } else {
        item.classList.add('blog-article-grid__item--bottom');
        this.updateCardVariant(item, 'standard');
      }
    });
  }

  updateCardVariant(item, variant) {
    const featuredCard = item.querySelector('[data-blog-card-variant="featured"]');
    const standardCard = item.querySelector('[data-blog-card-variant="standard"]');

    if (!featuredCard || !standardCard) {
      return;
    }

    const showFeatured = variant === 'featured';
    featuredCard.toggleAttribute('hidden', !showFeatured);
    standardCard.toggleAttribute('hidden', showFeatured);
  }

  toggleEmptyState() {
    if (!this.emptyState) {
      return;
    }

    const shouldShowEmpty = this.tabs.length > 0 && Boolean(this.activeFilter) && this.visibleItemsCount === 0;
    this.emptyState.toggleAttribute('hidden', !shouldShowEmpty);
  }

  async onShowMore() {
    if (this.isLoading) {
      return;
    }

    const nextUrl = this.dataset.nextUrl;
    const nextPage = this.dataset.nextPage;

    if (!nextUrl && !nextPage) {
      return;
    }

    this.isLoading = true;
    this.showMoreButton = this.querySelector('[data-blog-article-grid-show-more]');
    this.showMoreButton?.setAttribute('aria-busy', 'true');

    try {
      const url = new URL(nextUrl || window.location.href, window.location.origin);
      const currentUrl = new URL(window.location.href);

      currentUrl.searchParams.forEach((value, key) => {
        if (key !== 'page' && key !== 'section_id' && !url.searchParams.has(key)) {
          url.searchParams.set(key, value);
        }
      });

      if (nextPage) {
        url.searchParams.set('page', nextPage);
      }

      url.searchParams.set('section_id', this.dataset.sectionId);

      const response = await fetch(url.toString());
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newGrid =
        doc.querySelector(`blog-article-grid[data-section-id="${this.dataset.sectionId}"]`) ||
        doc.querySelector('blog-article-grid');

      if (!newGrid) {
        return;
      }

      const newItems = newGrid.querySelectorAll('.blog-article-grid__item');
      newItems.forEach((item) => {
        if (item.dataset.placeholder === 'true') {
          return;
        }

        this.itemsContainer.appendChild(document.importNode(item, true));
      });

      if (newGrid.dataset.nextUrl || newGrid.dataset.nextPage) {
        if (newGrid.dataset.nextUrl) {
          this.dataset.nextUrl = newGrid.dataset.nextUrl;
        }

        if (newGrid.dataset.nextPage) {
          this.dataset.nextPage = newGrid.dataset.nextPage;
        }
      } else {
        delete this.dataset.nextUrl;
        delete this.dataset.nextPage;
        this.showMoreButton?.remove();
      }

      this.applySort();
      this.applyFilter();
      this.updatePlaceholders();
      this.toggleEmptyState();
      this.updateLayoutClasses();
    } catch (error) {
      console.error('Failed to load more blog posts', error);
    } finally {
      this.isLoading = false;
      this.showMoreButton?.removeAttribute('aria-busy');
    }
  }
}

if (!window.customElements.get('blog-article-grid')) {
  window.customElements.define('blog-article-grid', BlogArticleGrid);
}
